import { eligibleForCancellationFee } from 'restaurantAdmin/components/cancelButton/apiHelpers';
import { RefundConfirmationModal } from 'restaurantAdmin/components/refundButton/RefundConfirmationModal';
import { CancelAndCollectFeeConfirmationModal } from 'restaurantAdmin/components/remediationForm/CancelAndCollectFeeConfirmationModal';
import { CancelAndWaiveFeeConfirmationModal } from 'restaurantAdmin/components/remediationForm/CancelAndWaiveFeeConfirmationModal';
import { CancelConfirmationModal } from 'restaurantAdmin/components/remediationForm/CancelConfirmationModal';
import { NoShowConfirmationModal } from 'restaurantAdmin/components/reservationOptions/NoShowConfirmationModal';
import { UpdateGuestCountModal } from 'restaurantAdmin/components/reservationOptions/UpdateGuestCountModal';
import { useRestaurant } from 'restaurantAdmin/context/useRestaurant';
import { useMessagingDrawer } from 'restaurantAdmin/messaging/hooks';
import {
  discardWalkIn,
  noShow,
  unseatReservation,
} from 'restaurantAdmin/reservations/apiHelpers';
import { Kebab, KebabOption } from '@components/kebab/Kebab';
import { errorToast } from '@components/toasts/Toasts';
import { ApiError } from '@shared/api/errors';
import { useAbortEffect } from '@shared/hooks/useAbortEffect';
import { useIsOpen } from '@shared/hooks/useIsOpen';
import { reportAppError } from '@shared/reportAppError';
import { isReservation, isWalkIn, type ServiceOccupant } from '../apiHelpers';
import { useServiceSearchParams } from '../hooks/useServiceSearchParams';
import { useUnadmitGuest } from '../hooks/useUnadmitGuest';
import { DiscardWalkInConfirmationModal } from '../sidePanel/DiscardWalkInConfirmationModal';
import { UndoCheckInGuestConfirmationModal } from '../sidePanel/reservationGuestSheet/UndoCheckInGuestConfirmationModal';
import { UnseatGuestConfirmationModal } from '../sidePanel/reservationGuestSheet/UnseatGuestConfirmationModal';
import { useReservationServiceContext } from '../state/ReservationServiceContext';
import actionStyles from './OccupantDetailsActions.scss';

export const OccupantDetailsOptionsMenu = ({
  occupant,
  fetchOccupant,
  allowActivationOfSeatMode,
}: {
  occupant: ServiceOccupant;
  fetchOccupant: () => void;
  allowActivationOfSeatMode: boolean;
}) => {
  const { closeDrawer, openSeatReservationDrawer, openSeatWalkInFormDrawer } =
    useServiceSearchParams();
  const { openMessagingDrawer } = useMessagingDrawer();
  const { id: restaurantId } = useRestaurant();
  const { refreshOccupants } = useReservationServiceContext();

  const {
    data: cancelFeeData = {
      isEligibleForCancellationFeeUponCancellation: false,
      eligibleForCancellationFeeUntil: '',
      cancellationPolicy: null,
    },
  } = useAbortEffect(
    {
      effect: async () => {
        if (!isReservation(occupant)) return undefined;
        return eligibleForCancellationFee(restaurantId, occupant.id);
      },
    },
    [restaurantId, occupant.id],
  );

  const {
    isOpen: isUnseatModalOpen,
    open: openUnseatModal,
    close: closeUnseatModal,
  } = useIsOpen();
  const {
    isOpen: isNoShowConfirmationModalOpen,
    open: openNoShowConfirmationModal,
    close: closeNoShowConfirmationModal,
  } = useIsOpen();
  const {
    isOpen: isCancelConfirmationModalOpen,
    open: openCancelConfirmationModal,
    close: closeCancelConfirmationModal,
  } = useIsOpen();
  const {
    isOpen: isCancelAndCollectFeeConfirmationModalOpen,
    open: openCancelAndCollectFeeConfirmationModal,
    close: closeCancelAndCollectFeeConfirmationModal,
  } = useIsOpen();
  const {
    isOpen: isCancelAndWaiveFeeConfirmationModalOpen,
    open: openCancelAndWaiveFeeConfirmationModal,
    close: closeCancelAndWaiveFeeConfirmationModal,
  } = useIsOpen();
  const {
    isOpen: isRefundConfirmationModalOpen,
    open: openRefundConfirmationModal,
    close: closeRefundConfirmationModal,
  } = useIsOpen();
  const {
    isOpen: isDiscardWalkInModalOpen,
    open: openDiscardWalkInModal,
    close: closeDiscardWalkInModal,
  } = useIsOpen();
  const {
    isOpen: isUpdateGuestCountModalOpen,
    open: openUpdateGuestCountModal,
    close: closeUpdateGuestCountModal,
  } = useIsOpen();
  const {
    isOpen: isUndoCheckInModalOpen,
    open: openUndoCheckInModal,
    close: closeUndoCheckInModal,
  } = useIsOpen();

  const unadmitGuestMutation = useUnadmitGuest(occupant.id);

  const handleUnseatReservation = async (): Promise<void> => {
    try {
      await unseatReservation({
        restaurantId,
        reservationId: occupant.id,
      });

      closeDrawer();
    } catch (error) {
      if (error instanceof ApiError) {
        errorToast({ message: error.message });
      } else {
        errorToast({
          message: 'Failed to unseat guest. Please try again.',
        });
      }
      reportAppError(error);
    }
  };

  const handleOnClickNoShow = async () => {
    if (!isReservation(occupant)) return;

    try {
      await noShow({
        restaurantId,
        reservationId: occupant.id,
      });

      closeDrawer();
    } catch (error) {
      if (error instanceof ApiError) {
        errorToast({ message: error.message });
      } else {
        errorToast({
          message: 'Failed to flag reservation as no show. Please try again.',
        });
      }
      reportAppError(error);
    }
  };

  const handleDiscardWalkIn = () => {
    if (!isWalkIn(occupant)) return;
    void (async () => {
      try {
        await discardWalkIn({
          restaurantId,
          walkInId: occupant.id,
        });
        closeDiscardWalkInModal();
        closeDrawer();
      } catch (error) {
        if (error instanceof ApiError) {
          errorToast({ message: error.message });
        } else {
          errorToast({
            message: 'Failed to discard walk-in. Please try again.',
          });
        }
      }
    })();
  };

  // Cancelling a reservation should remove it from the list of occupants on the
  // service page. Refreshing the occupants list causes the canceled reservation
  // to disappear.
  const handleAfterCancelConfirm = () => {
    closeCancelConfirmationModal();
    refreshOccupants();
    closeDrawer();
  };
  const handleAfterCollectConfirm = () => {
    closeCancelAndCollectFeeConfirmationModal();
    refreshOccupants();
    closeDrawer();
  };
  const handleAfterWaiveConfirm = () => {
    closeCancelAndWaiveFeeConfirmationModal();
    refreshOccupants();
    closeDrawer();
  };

  const handleAfterRefundConfirm = () => {
    closeRefundConfirmationModal();
    // Instead of closing the drawer, re-fetch the occupant to update the
    // receipt with the refund amount.
    fetchOccupant();
  };

  const handleOnChangeSeat = () => {
    if (isReservation(occupant)) {
      openSeatReservationDrawer();
    } else {
      openSeatWalkInFormDrawer();
    }
  };

  const handleOpenMessageGuestModal = () => {
    if (isReservation(occupant)) {
      openMessagingDrawer([occupant.guest.id]);
    }
  };

  const handleUndoCheckInGuest = () => {
    unadmitGuestMutation.mutate(undefined, {
      onSuccess: () => {
        closeUndoCheckInModal();
        refreshOccupants();
      },
    });
  };

  const isEligibleForRefund = () => {
    if (!isReservation(occupant)) return false;
    const { refundAmount, price } = occupant.transactionDetails;
    return !refundAmount && price > 0;
  };

  const isMessageable = isReservation(occupant) && !!occupant.guest.phone;
  const isOccupantAdmitted =
    isReservation(occupant) && occupant.serviceStatus === 'admitted';

  const options = [
    occupant.seatedTableName &&
      allowActivationOfSeatMode && {
        label: 'Change Seat',
        onClick: handleOnChangeSeat,
      },
    occupant.seatedTableName &&
      isReservation(occupant) && {
        label: 'Unseat Guest',
        onClick: openUnseatModal,
      },
    isOccupantAdmitted && {
      label: 'Undo Check-in',
      onClick: openUndoCheckInModal,
    },
    isWalkIn(occupant) && {
      label: 'Discard Walk-in',
      onClick: openDiscardWalkInModal,
    },
    isReservation(occupant) && {
      label: 'Message Guest',
      onClick: handleOpenMessageGuestModal,
      disabled: !isMessageable,
    },
    isReservation(occupant) && {
      label: 'No Show',
      onClick: openNoShowConfirmationModal,
    },
    isReservation(occupant) && {
      label: 'Cancel',
      onClick: openCancelConfirmationModal,
    },
    isReservation(occupant) &&
      isEligibleForRefund() && {
        label: 'Refund',
        onClick: openRefundConfirmationModal,
      },
    cancelFeeData.isEligibleForCancellationFeeUponCancellation && {
      label: 'Cancel & Collect Fee',
      onClick: openCancelAndCollectFeeConfirmationModal,
    },
    cancelFeeData.isEligibleForCancellationFeeUponCancellation && {
      label: 'Cancel & Waive Fee',
      onClick: openCancelAndWaiveFeeConfirmationModal,
    },
    {
      label: 'Edit Guest Count',
      onClick: openUpdateGuestCountModal,
    },
  ].filter(Boolean) as KebabOption[];

  return (
    <>
      <Kebab
        triggerLabel="Options"
        ariaLabelPrefix="Occupant details"
        options={options}
        side="bottom"
        triggerClassName={actionStyles.button}
      />
      <UnseatGuestConfirmationModal
        closeModal={closeUnseatModal}
        isOpen={isUnseatModalOpen}
        onUnseatGuest={handleUnseatReservation}
      />
      <UndoCheckInGuestConfirmationModal
        closeModal={closeUndoCheckInModal}
        isOpen={isUndoCheckInModalOpen}
        onUndoCheckInGuest={handleUndoCheckInGuest}
      />
      {isWalkIn(occupant) && (
        <DiscardWalkInConfirmationModal
          handleDiscardWalkIn={handleDiscardWalkIn}
          isLoading={false} // TODO: remove loading state?
          isOpen={isDiscardWalkInModalOpen}
          closeModal={closeDiscardWalkInModal}
        />
      )}
      {isReservation(occupant) && (
        <>
          <NoShowConfirmationModal
            isOpen={isNoShowConfirmationModalOpen}
            closeModal={closeNoShowConfirmationModal}
            handleOnClickNoShow={handleOnClickNoShow}
          />
          <CancelConfirmationModal
            cancellationPolicy={cancelFeeData.cancellationPolicy}
            closeModal={closeCancelConfirmationModal}
            eligibleForCancellationFeeUntil={
              cancelFeeData.eligibleForCancellationFeeUntil
            }
            handleAfterConfirm={handleAfterCancelConfirm}
            isEligibleForCancellationFeeUponCancellation={
              cancelFeeData.isEligibleForCancellationFeeUponCancellation
            }
            isOpen={isCancelConfirmationModalOpen}
            reservationId={occupant.id}
          />
          <CancelAndCollectFeeConfirmationModal
            cancellationPolicy={cancelFeeData.cancellationPolicy!}
            closeModal={closeCancelAndCollectFeeConfirmationModal}
            handleAfterConfirm={handleAfterCollectConfirm}
            isOpen={isCancelAndCollectFeeConfirmationModalOpen}
            reservationId={occupant.id}
          />
          <CancelAndWaiveFeeConfirmationModal
            cancellationPolicy={cancelFeeData.cancellationPolicy!}
            closeModal={closeCancelAndWaiveFeeConfirmationModal}
            handleAfterConfirm={handleAfterWaiveConfirm}
            isOpen={isCancelAndWaiveFeeConfirmationModalOpen}
            reservationId={occupant.id}
          />
          <RefundConfirmationModal
            disablePortal={false}
            closeModal={closeRefundConfirmationModal}
            handleAfterConfirm={handleAfterRefundConfirm}
            isOpen={isRefundConfirmationModalOpen}
            reservationId={occupant.id}
          />
        </>
      )}
      <UpdateGuestCountModal
        closeModal={closeUpdateGuestCountModal}
        isOpen={isUpdateGuestCountModalOpen}
        occupant={occupant}
        onSuccess={fetchOccupant}
      />
    </>
  );
};
